import React, { useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import billingService from '../../api/billingService';
import { formatCurrency } from '../../utils/formatters';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const BillingReports = () => {
  const [mrrData, setMrrData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [mrr, rev, out] = await Promise.all([
          billingService.getMRRReport(),
          billingService.getRevenueReport(),
          billingService.getOutstandingInvoices(),
        ]);
        if (mrr.success) setMrrData(mrr.data);
        if (rev.success) setRevenueData(rev.data);
        if (out.success) setOutstanding(out.data);
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  const mrrChartData = {
    labels: mrrData.map(d => d.month),
    datasets: [{
      label: 'MRR',
      data: mrrData.map(d => d.amount),
      backgroundColor: '#0d6efd',
      borderRadius: 6,
    }],
  };

  const revChartData = {
    labels: revenueData.map(d => d.month).reverse(),
    datasets: [{
      label: 'Collected Revenue',
      data: revenueData.map(d => d.total).reverse(),
      borderColor: '#198754',
      backgroundColor: 'rgba(25, 135, 84, 0.1)',
      fill: true,
      tension: 0.4,
    }],
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-700 mb-0">Reports & Analytics</h4>
        <button className="btn btn-light"><i className="bi bi-download me-2"></i> Export All</button>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm">
            <div className="card-header border-0 bg-transparent pt-4 px-4">
              <h5 className="fw-700 mb-0">MRR Trend</h5>
            </div>
            <div className="card-body p-4">
              <div style={{ height: '300px' }}>
                <Bar data={mrrChartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm">
            <div className="card-header border-0 bg-transparent pt-4 px-4">
              <h5 className="fw-700 mb-0">Revenue Collection</h5>
            </div>
            <div className="card-body p-4">
              <div style={{ height: '300px' }}>
                <Line data={revChartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header border-0 bg-transparent pt-4 px-4">
          <h5 className="fw-700 mb-0">Outstanding Invoices</h5>
        </div>
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {outstanding.map((inv) => (
                <tr key={inv._id}>
                  <td><span className="fw-600">{inv.invoice_number}</span></td>
                  <td>{inv.customer?.company_name || 'N/A'}</td>
                  <td>{new Date(inv.due_date).toLocaleDateString()}</td>
                  <td>{formatCurrency(inv.grand_total)}</td>
                  <td className="text-danger fw-600">{formatCurrency(inv.balance_due)}</td>
                  <td><span className="badge badge-soft-warning">{inv.status.toUpperCase()}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BillingReports;
